import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import PermissionChecklist from '../shared/PermissionChecklist';
import TemplateAlertBanner from './TemplateAlertBanner';
import PERMISSION_KEYS from '../../constants/permission-keys';
import {
    getPermissionLabel,
    PERMISSION_DESCRIPTION_KEYS,
} from '../../constants/permission-meta';
import type { PermissionTemplate } from '../../services/api/departmentPermissionService';
import {
    computeAlignment,
    type AlignmentUser,
} from '../../utils/permissionAlignment';

interface DeptPermissionTemplateDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    roleName: string;
    template: PermissionTemplate | null;
    users: AlignmentUser[];
    canEdit: boolean;
    onApply: (
        deptPermissions: string[],
        mode: 'default' | 'custom'
    ) => Promise<void>;
    onAcknowledge: () => void;
}

const ALL_PERMISSIONS = Object.values(PERMISSION_KEYS) as string[];

export default function DeptPermissionTemplateDrawer({
    open,
    onOpenChange,
    roleName,
    template,
    users,
    canEdit,
    onApply,
    onAcknowledge,
}: DeptPermissionTemplateDrawerProps) {
    const { t } = useTranslation();
    const [mode, setMode] = useState<'default' | 'custom'>('default');
    const [selected, setSelected] = useState<string[]>([]);
    const [applying, setApplying] = useState(false);

    useEffect(() => {
        if (!template || !open) return;
        setMode(template.mode === 'custom' ? 'custom' : 'default');
        // Custom starts from the currently effective permissions so switching
        // from NordInsight defaults begins at the recommended set.
        setSelected([...template.deptPermissions]);
    }, [template, open]);

    const sections = useMemo(
        () => [
            {
                items: ALL_PERMISSIONS.map((key) => {
                    const descKey = PERMISSION_DESCRIPTION_KEYS[key];
                    return {
                        key,
                        label: getPermissionLabel(key, t),
                        description: descKey ? t(descKey) : undefined,
                    };
                }),
            },
        ],
        [t]
    );

    const extraUsers = useMemo(() => {
        if (!template) return [];
        return users
            .map((u) => ({
                user: u,
                extra: computeAlignment(
                    u.deptPermissions,
                    template.deptPermissions
                ).extra,
            }))
            .filter((e) => e.extra.length > 0);
    }, [users, template]);

    if (!template) return null;

    const globalPermissions = template.globalPermissions ?? [];
    const isDefault = mode === 'default';

    const handleApply = async () => {
        setApplying(true);
        try {
            await onApply(isDefault ? [] : selected, mode);
            onOpenChange(false);
        } finally {
            setApplying(false);
        }
    };

    const modeOptions: {
        value: 'default' | 'custom';
        label: string;
        hint: string;
    }[] = [
        {
            value: 'default',
            label: t('facilities.departmentRoles.applyDrawer.modeNordInsight'),
            hint: t('facilities.departmentRoles.applyDrawer.modeNordInsightHint'),
        },
        {
            value: 'custom',
            label: t('facilities.departmentRoles.applyDrawer.modeCustom'),
            hint: t('facilities.departmentRoles.applyDrawer.modeCustomHint'),
        },
    ];

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="flex w-full flex-col gap-0 p-0 sm:w-[480px] sm:max-w-[480px]"
            >
                <SheetHeader className="border-b border-subtle/60 px-6 py-4">
                    <SheetTitle>
                        {t('facilities.departmentRoles.applyDrawer.title', {
                            role: roleName,
                        })}
                    </SheetTitle>
                    <SheetDescription>
                        {t('facilities.departmentRoles.applyDrawer.description')}
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 space-y-5 overflow-y-auto px-6 py-4">
                    {/* Unacknowledged template changes */}
                    {template.hasUnacknowledgedChanges && mode === 'default' && (
                        <TemplateAlertBanner
                            alertType={template.alertType}
                            roleName={roleName}
                            onReview={onAcknowledge}
                            actionLabel={t(
                                'facilities.departmentRoles.alerts.acknowledge'
                            )}
                        />
                    )}

                    {/* Mode toggle */}
                    <div className="grid grid-cols-2 gap-2">
                        {modeOptions.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                disabled={!canEdit}
                                onClick={() => {
                                    setMode(opt.value);
                                    if (
                                        opt.value === 'custom' &&
                                        selected.length === 0
                                    ) {
                                        setSelected([...globalPermissions]);
                                    }
                                }}
                                className={`rounded-lg border p-3 text-left transition-colors ${
                                    mode === opt.value
                                        ? 'border-sky-500/50 bg-sky-500/[0.08]'
                                        : 'border-white/[0.08] bg-white/[0.02]'
                                } ${
                                    canEdit
                                        ? 'hover:bg-white/[0.04]'
                                        : 'cursor-default opacity-70'
                                }`}
                            >
                                <span className="block text-sm font-semibold">
                                    {opt.label}
                                </span>
                                <span className="mt-0.5 block text-xs text-muted-foreground">
                                    {opt.hint}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Info banner */}
                    {canEdit && mode === 'custom' && (
                        <div className="flex items-start gap-2.5 rounded-lg border border-sky-500/20 bg-sky-500/[0.06] px-3.5 py-2.5">
                            <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-400" />
                            <p
                                className="text-xs leading-relaxed text-foreground/80"
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'facilities.departmentRoles.applyDrawer.infoBanner',
                                        { role: roleName }
                                    ),
                                }}
                            />
                        </div>
                    )}

                    {/* Permission list */}
                    <PermissionChecklist
                        sections={sections}
                        selected={isDefault ? globalPermissions : selected}
                        onChange={setSelected}
                        disabled={isDefault || !canEdit}
                    />

                    {/* Users with extra permissions */}
                    {extraUsers.length > 0 && (
                        <div className="rounded-lg border border-white/[0.07] bg-white/[0.02]">
                            <p className="border-b border-white/[0.06] px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                {t(
                                    'facilities.departmentRoles.usersWithExtra',
                                    { count: extraUsers.length }
                                )}
                            </p>
                            {extraUsers.map(({ user, extra }) => (
                                <div
                                    key={user._id}
                                    className="flex items-center gap-3 border-b border-white/[0.05] px-4 py-2 last:border-b-0"
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium">
                                            {user.name || user.email}
                                        </p>
                                        {user.name && (
                                            <p className="truncate text-xs text-muted-foreground">
                                                {user.email}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap justify-end gap-1">
                                        {extra.map((p) => (
                                            <Badge
                                                key={p}
                                                variant="outline"
                                                className="px-1.5 py-0 text-[10px]"
                                            >
                                                {getPermissionLabel(p, t)}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            <p className="px-4 py-2.5 text-xs text-muted-foreground">
                                {t('facilities.departmentRoles.extraUsersHint')}
                            </p>
                        </div>
                    )}
                </div>

                <SheetFooter className="border-t border-subtle/60 px-6 py-4">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={applying}
                    >
                        {t('facilities.departmentRoles.applyDrawer.cancel')}
                    </Button>
                    {canEdit && (
                        <Button onClick={handleApply} disabled={applying}>
                            {applying
                                ? t('facilities.departmentRoles.applyDrawer.applying')
                                : t('facilities.departmentRoles.applyDrawer.apply')}
                        </Button>
                    )}
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
